import { ref, onMounted, onUnmounted, type Ref } from 'vue';

export interface UseDragScrollOptions {
    /**
     * Minimum distance in pixels to consider it a drag (not a click)
     * Default: 5
     */
    dragThreshold?: number;

    /**
     * Callback when drag state changes
     */
    onDragStateChange?: (isDragging: boolean) => void;
}

export interface UseDragScrollReturn {
    /**
     * Ref to attach to the scrollable container element
     */
    scrollContainer: Ref<HTMLElement | null>;

    /**
     * Whether the user is currently dragging
     */
    isDragging: Ref<boolean>;
}

/**
 * Composable for drag-to-scroll functionality with mouse and touch support
 *
 * @example
 * ```vue
 * <script setup>
 * import { useDragScroll } from '@src/Common/Composables/useDragScroll';
 *
 * const { scrollContainer, isDragging } = useDragScroll({
 *   dragThreshold: 5,
 * });
 * </script>
 *
 * <template>
 *   <div ref="scrollContainer" class="overflow-x-auto">
 *     <a href="/link1">Item 1</a>
 *     <a href="/link2">Item 2</a>
 *   </div>
 * </template>
 * ```
 */
export function useDragScroll(options?: UseDragScrollOptions): UseDragScrollReturn {
    const scrollContainer = ref<HTMLElement | null>(null);
    const isDragging = ref(false);

    const DRAG_THRESHOLD = options?.dragThreshold ?? 5;

    // Drag state tracking
    let isDown = false;
    let startX = 0;
    let startY = 0;
    let scrollLeft = 0;
    let wasDragging = false; // Track if user actually dragged (exceeded threshold)

    /**
     * Start dragging - common logic for mouse and touch
     */
    const startDragging = (clientX: number, clientY: number) => {
        if (!scrollContainer.value) return;

        isDown = true;
        isDragging.value = false;
        wasDragging = false;
        startX = clientX;
        startY = clientY;
        scrollLeft = scrollContainer.value.scrollLeft;

        scrollContainer.value.classList.add('cursor-grabbing');
    };

    /**
     * Handle dragging movement - common logic for mouse and touch
     */
    const moveDragging = (clientX: number, clientY: number) => {
        if (!isDown || !scrollContainer.value) return;

        const walkX = clientX - startX;
        const walkY = clientY - startY;

        // Check if movement exceeds threshold
        if (Math.abs(walkX) > DRAG_THRESHOLD || Math.abs(walkY) > DRAG_THRESHOLD) {
            isDragging.value = true;
            wasDragging = true;
            options?.onDragStateChange?.(true);

            // Disable pointer events on links as fallback prevention
            const links = scrollContainer.value.querySelectorAll('a, [role="link"]');
            links.forEach((link) => {
                (link as HTMLElement).style.pointerEvents = 'none';
            });
        }

        // Only scroll if we've exceeded the threshold
        if (isDragging.value) {
            scrollContainer.value.scrollLeft = scrollLeft - walkX;
        }
    };

    /**
     * Stop dragging - common logic for mouse and touch
     */
    const stopDragging = () => {
        if (!isDown || !scrollContainer.value) return;

        // CRITICAL: Stop any ongoing scroll momentum
        const currentScrollLeft = scrollContainer.value.scrollLeft;
        scrollContainer.value.scrollLeft = currentScrollLeft;

        // Force momentum stop by temporarily disabling scroll
        scrollContainer.value.style.overflowX = 'hidden';
        requestAnimationFrame(() => {
            if (scrollContainer.value) {
                scrollContainer.value.style.overflowX = '';
            }
        });

        isDown = false;
        scrollContainer.value.classList.remove('cursor-grabbing');

        // Re-enable pointer events on links after drag
        requestAnimationFrame(() => {
            if (scrollContainer.value) {
                const links = scrollContainer.value.querySelectorAll('a, [role="link"]');
                links.forEach((link) => {
                    (link as HTMLElement).style.pointerEvents = '';
                });
            }
        });

        // Delay resetting isDragging to allow click handler to check it
        setTimeout(() => {
            if (isDragging.value) {
                options?.onDragStateChange?.(false);
            }
            isDragging.value = false;
        }, 10);

        // Reset wasDragging after click events are processed
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                wasDragging = false;
            });
        });
    };

    // Mouse event handlers
    const onMouseDown = (e: MouseEvent) => {
        e.preventDefault(); // Prevent native drag from starting
        startDragging(e.clientX, e.clientY);
    };

    const onMouseMove = (e: MouseEvent) => {
        if (!isDown) return;
        e.preventDefault();
        moveDragging(e.clientX, e.clientY);
    };

    const onMouseUp = () => {
        stopDragging();
    };

    const onMouseLeave = () => {
        stopDragging();
    };

    // Touch event handlers
    const onTouchStart = (e: TouchEvent) => {
        if (e.touches.length !== 1) return; // Only handle single touch
        e.preventDefault(); // Prevent native touch behaviors
        const touch = e.touches[0];
        startDragging(touch.clientX, touch.clientY);
    };

    const onTouchMove = (e: TouchEvent) => {
        if (!isDown || e.touches.length !== 1) return;
        const touch = e.touches[0];

        // Only prevent default if we're actively dragging
        if (isDragging.value) {
            e.preventDefault();
        }

        moveDragging(touch.clientX, touch.clientY);
    };

    const onTouchEnd = () => {
        stopDragging();
    };

    const onTouchCancel = () => {
        stopDragging();
    };

    /**
     * Handle click events in capture phase to prevent navigation during drag
     * This fires BEFORE Inertia.js or other bubble-phase handlers
     */
    const handleClickCapture = (e: Event) => {
        if (isDragging.value || wasDragging) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
        }
    };

    onMounted(() => {
        if (!scrollContainer.value) return;

        // Click prevention in CAPTURE phase (fires before bubble phase)
        scrollContainer.value.addEventListener('click', handleClickCapture, true);

        // Mouse events
        scrollContainer.value.addEventListener('mousedown', onMouseDown);
        scrollContainer.value.addEventListener('mousemove', onMouseMove);
        scrollContainer.value.addEventListener('mouseup', onMouseUp);
        scrollContainer.value.addEventListener('mouseleave', onMouseLeave);

        // Touch events
        scrollContainer.value.addEventListener('touchstart', onTouchStart, { passive: false });
        scrollContainer.value.addEventListener('touchmove', onTouchMove, { passive: false });
        scrollContainer.value.addEventListener('touchend', onTouchEnd);
        scrollContainer.value.addEventListener('touchcancel', onTouchCancel);
    });

    onUnmounted(() => {
        if (!scrollContainer.value) return;

        // Remove click prevention
        scrollContainer.value.removeEventListener('click', handleClickCapture, true);

        // Mouse events
        scrollContainer.value.removeEventListener('mousedown', onMouseDown);
        scrollContainer.value.removeEventListener('mousemove', onMouseMove);
        scrollContainer.value.removeEventListener('mouseup', onMouseUp);
        scrollContainer.value.removeEventListener('mouseleave', onMouseLeave);

        // Touch events
        scrollContainer.value.removeEventListener('touchstart', onTouchStart);
        scrollContainer.value.removeEventListener('touchmove', onTouchMove);
        scrollContainer.value.removeEventListener('touchend', onTouchEnd);
        scrollContainer.value.removeEventListener('touchcancel', onTouchCancel);
    });

    return {
        scrollContainer,
        isDragging,
    };
}
